/**
 * ----------------------------------------------------------------------------------------------------------------------
 * THE RETINOL REPORT — page behaviour
 * ----------------------------------------------------------------------------------------------------------------------
 *
 * Loaded ONCE, deferred, from sections/retinol-report-protocol-nav.liquid. It is the only JavaScript the page ships.
 * Precedent: snippets/visual-discount.liquid, which ships its own deferred asset alongside its own stylesheet.
 *
 * No dependencies. No jQuery (the theme loads it; we do not use it). Vanilla ES2019 that Safari 14 can parse.
 * Nothing is attached to `window`.
 *
 * EVERY behaviour is progressive enhancement. With JavaScript off the page is complete: the nav items are real
 * anchors, the discount chip is a real /discount/ link, the offer section carries its own add-to-cart form, and the two JS-only affordances (the SAVE THIS print control and the product dock)
 * are rendered `hidden` in Liquid and only revealed here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * DATA-ATTRIBUTE CONTRACT (sections must emit exactly these — classes are for CSS, never for binding)
 * ------------------------------------------------------------------------------------------------------------
 *
 * (a) SCROLL-SPY — retinol-report-protocol-nav.liquid
 *     [data-rr-nav]                  the <nav> element wrapping the rail
 *     [data-rr-nav-scroller]         optional: the overflow-x element on mobile (defaults to [data-rr-nav])
 *     [data-rr-nav-item]             each <a href="#anchor"> in the rail
 *     -> active item receives class .rr-nav__item--active and aria-current="true"
 *
 * (b) REMOVED — click-to-copy for the discount code. The USE CODE RETINOL30 chip and its copy
 *     button were deleted from retinol-report-offer.liquid (plan §4, "CTAs being deleted"): the
 *     chip bounced a buyer onto the PDP at the decision moment and the copy button optimised for
 *     "save it for later". Every add-to-cart on the page is the permalink that applies the code,
 *     so there is nothing left to copy. There is no [data-rr-copy] on the page.
 *
 * (c) REMOVED — the checklist counter. retinol-report-checklist.liquid was merged into
 *     retinol-report-ladder.liquid (plan §2 row 5) and its eight tick-boxes became a static
 *     third-person list: Meta's personal-attributes standard does not allow a diagnostic quiz
 *     addressed to the reader's own skin (§8). There is no [data-rr-checklist] on the page and
 *     the behaviour is deleted, not disabled.
 *
 * (d) PERSISTENT PRODUCT DOCK — retinol-report-offer.liquid (the page's ONE persistent buy affordance)
 *     [data-rr-dock]                         the fixed dock, rendered with the `hidden` attribute
 *     [data-rr-dock-after="rr-hero-buy"]     id of the element that must scroll PAST before it reveals
 *     [data-rr-dock-depth="0.38"]            fraction of the document that must ALSO be behind the reader
 *     [data-rr-dock-suppress="offer"]        id of the section that hides it again while it is in view
 *     [data-rr-dock-until="close"]           id of the LAST in-flow add-to-cart section; from the moment
 *                                            its top enters the viewport the dock retires for good, so the
 *                                            dock and an inline ADD TO CART are never on screen together
 *     [data-rr-dock-dismiss]                 the dismiss <button type="button"> inside the dock
 *     -> sets --rr-dock-lift on the dock and toggles .rr-dock--blocked when a third-party overlay
 *        covers the dock's own ADD TO CART button (see the occlusion notes in (d) below)
 *
 * (e) PRINT — retinol-report-protocol-nav.liquid
 *     [data-rr-print]                        the SAVE THIS <button type="button">, rendered with `hidden`
 *
 * (f) SHARE / PRINT THE FLOW CARD — retinol-report-protocol.liquid
 *     [data-rr-share]                        the mobile corner-tag <button type="button">, rendered `hidden`
 *     [data-rr-share-title="Your retinol night"]  optional, on the button; the share sheet's title
 *     [data-rr-share-static]                 the no-JS <p> label it replaces (hidden once JS runs)
 */

(function () {
  'use strict';

  // The tag that loads this file moved from retinol-report-protocol-nav.liquid (deleted from the
  // flow, plan §4) to retinol-report-offer.liquid, which renders the dock. A section that is
  // reordered or duplicated in the theme editor must never give the page two sets of listeners.
  if (window.__rrReportBehaviours) return;
  window.__rrReportBehaviours = true;

  var NAV = '[data-rr-nav]';
  var NAV_ITEM = '[data-rr-nav-item]';
  var NAV_SCROLLER = '[data-rr-nav-scroller]';
  var ACTIVE_CLASS = 'rr-nav__item--active';

  var DOCK = '[data-rr-dock]';
  var DOCK_ATC = '.rr-dock__atc';
  var DOCK_DISMISS = '[data-rr-dock-dismiss]';
  var DOCK_VISIBLE_CLASS = 'rr-dock--visible';
  var DOCK_BLOCKED_CLASS = 'rr-dock--blocked';
  var DOCK_STORAGE_KEY = 'rr:dock-dismissed';
  var DOCK_LIFT_PROP = '--rr-dock-lift';
  var DOCK_LIFT_GAP = 8;         // px of air between the dock and whatever it had to climb over
  var DOCK_LIFT_MAX_RATIO = 0.28; // an occluder needing more lift than this is a dialog, not furniture
  var DOCK_RECHECK_MS = 800;     // consent banners and chat launchers mount late and leave late
  var DOCK_RECHECK_FOR_MS = 45000;
  var DOCK_DEPTH_DEFAULT = 0.38; // fraction of the document that must be behind the reader (see (d))

  var PRINT = '[data-rr-print]';
  var EXPAND = '[data-rr-expand]';

  var SHARE = '[data-rr-share]';
  var SHARE_STATIC = '[data-rr-share-static]';

  var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  /* ----------------------------------------------------------------------------------------------------------
   * Small helpers
   * -------------------------------------------------------------------------------------------------------- */

  function qs(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function qsa(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function matchMediaSafe(query) {
    if (typeof window.matchMedia !== 'function') {
      return null;
    }
    return window.matchMedia(query);
  }

  function prefersReducedMotion() {
    var mql = matchMediaSafe(REDUCED_MOTION_QUERY);
    return !!(mql && mql.matches);
  }

  // sessionStorage throws in Safari private mode and when cookies are blocked. Never let it break the page.
  function sessionGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function sessionSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      /* no-op */
    }
  }

  // rAF-throttled callback, so scroll handlers never run more than once per frame.
  function throttleToFrame(callback) {
    var queued = false;
    return function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        callback();
      });
    };
  }

  /* ----------------------------------------------------------------------------------------------------------
   * (a) Scroll-spy on the protocol nav
   * -------------------------------------------------------------------------------------------------------- */

  function initScrollSpy() {
    var nav = qs(NAV);
    if (!nav) return;

    var scroller = qs(NAV_SCROLLER, nav) || nav;

    // Only items whose href resolves to a real element on this page take part.
    var entries = [];
    qsa(NAV_ITEM, nav).forEach(function (link) {
      var href = link.getAttribute('href') || '';
      if (href.charAt(0) !== '#' || href.length < 2) return;

      var target = null;
      try {
        target = document.getElementById(decodeURIComponent(href.slice(1)));
      } catch (error) {
        target = document.getElementById(href.slice(1));
      }
      if (!target) return;

      entries.push({ link: link, target: target });
    });

    if (!entries.length) return;

    var current = null;

    function setActive(entry) {
      if (entry === current) return;
      current = entry;

      entries.forEach(function (candidate) {
        var isActive = candidate === entry;
        candidate.link.classList.toggle(ACTIVE_CLASS, isActive);
        if (isActive) {
          candidate.link.setAttribute('aria-current', 'true');
        } else {
          candidate.link.removeAttribute('aria-current');
        }
      });

      revealInScroller(entry.link);
    }

    // On mobile the rail is a horizontal scroller; keep the active item in view.
    // This is scrolling we trigger, so it honours prefers-reduced-motion.
    function revealInScroller(link) {
      if (scroller.scrollWidth <= scroller.clientWidth) return;

      var linkBox = link.getBoundingClientRect();
      var scrollerBox = scroller.getBoundingClientRect();
      var delta = 0;

      if (linkBox.left < scrollerBox.left) {
        delta = linkBox.left - scrollerBox.left - 16;
      } else if (linkBox.right > scrollerBox.right) {
        delta = linkBox.right - scrollerBox.right + 16;
      }
      if (delta === 0) return;

      var left = scroller.scrollLeft + delta;
      if (typeof scroller.scrollTo === 'function') {
        scroller.scrollTo({ left: left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      } else {
        scroller.scrollLeft = left;
      }
    }

    /* ------------------------------------------------------------------------------------------------------
     * Landing offset and probe line — ONE formula, which is the whole bug fix.
     *
     * The old code used two different numbers for the same edge. An anchor landed at a STATIC CSS
     * scroll-margin (~215px on desktop, ~76px on mobile) while the spy probed at a LIVE line
     * (nav.getBoundingClientRect().bottom + 16). The theme's header hides on scroll-down by translating
     * itself -100%, so by the time a downward jump finished, the live line had collapsed to ~80px (or to
     * 16px on mobile, where the rail is not sticky at all and had scrolled clean off the top). The target
     * you had just jumped to sat BELOW the line, the loop below therefore never matched it, and the item
     * above it stayed lit — click 03, watch 02 stay highlighted.
     *
     * Both numbers now come from chromeExtent(), which measures the rail's own live geometry:
     *
     *   header layout height  (offsetHeight, NOT rect.bottom — offsetHeight is immune to the hide
     *                          transform, so it does not collapse mid-scroll)
     *   + the live header bottom, whichever is greater (this is what folds in a sticky announcement bar)
     *   + the rail's own height, but ONLY while the rail is actually sticky (it is not, below 741px)
     *   + a 16px breathing gap
     *
     * and it is monotonic within a viewport size: it never returns less than the largest extent already
     * seen, because a value that shrinks after a jump is exactly what stranded the old probe. It resets
     * on resize, where the geometry genuinely changed.
     *
     * The probe line is that same extent plus a small tolerance, so a target parked at the landing offset
     * is always at or above the line it is tested against. The two can no longer disagree.
     * ---------------------------------------------------------------------------------------------- */

    var PROBE_TOLERANCE = 24;
    var CHROME_GAP = 16;
    var themeHeader = document.querySelector('.shopify-section--header');
    var navWrapper = nav.closest ? nav.closest('.shopify-section') : null;
    if (!navWrapper) navWrapper = nav;
    var chromeSeen = 0;

    function isStuck(element) {
      if (!window.getComputedStyle) return false;
      var position = window.getComputedStyle(element).position;
      return position === 'sticky' || position === '-webkit-sticky' || position === 'fixed';
    }

    function chromeExtent() {
      var headerExtent = 0;
      if (themeHeader) {
        var liveBottom = themeHeader.getBoundingClientRect().bottom;
        if (liveBottom < 0) liveBottom = 0;
        headerExtent = Math.max(themeHeader.offsetHeight || 0, liveBottom);
      }

      var railExtent = isStuck(navWrapper) ? navWrapper.offsetHeight || 0 : 0;

      var extent = headerExtent + railExtent + CHROME_GAP;

      // Never let it shrink: a collapsing offset is the original bug.
      var cap = window.innerHeight / 3;
      if (extent > cap) extent = cap;
      if (extent > chromeSeen) chromeSeen = extent;
      return chromeSeen;
    }

    function probeLine() {
      return chromeExtent() + PROBE_TOLERANCE;
    }

    function isRendered(entry) {
      return entry.link.getClientRects().length > 0;
    }

    function update() {
      var line = probeLine();
      var atPageBottom = window.innerHeight + window.pageYOffset >= document.documentElement.scrollHeight - 2;

      if (atPageBottom) {
        var rendered = entries.filter(isRendered);
        if (!rendered.length) rendered = entries;
        setActive(rendered[rendered.length - 1]);
        return;
      }

      // Both a desktop-only and a mobile-only item can point at the same anchor. Only the one that
      // is actually rendered at this viewport may take the active state.
      var visibleEntries = entries.filter(isRendered);
      if (!visibleEntries.length) visibleEntries = entries;

      var match = visibleEntries[0];
      for (var i = 0; i < visibleEntries.length; i++) {
        if (visibleEntries[i].target.getBoundingClientRect().top <= line) {
          match = visibleEntries[i];
        }
      }
      setActive(match);
    }

    /* ------------------------------------------------------------------------------------------------------
     * Click interception.
     *
     * Progressive enhancement, not a replacement: every item is still a real <a href="#anchor"> and the CSS
     * scroll-margin-top on .anchor still lands it correctly with JavaScript off. This only takes over when
     * the script is running, because only the script can measure where the chrome actually is right now.
     *
     * 1. scroll to a position computed from chromeExtent() above, honouring prefers-reduced-motion
     * 2. write the hash with history.pushState, so the URL updates without the browser performing its own
     *    second jump to the element (assigning location.hash would re-scroll, at the wrong offset)
     * 3. set the active item IMMEDIATELY rather than waiting for the scroll handler to infer it, and hold
     *    it for the length of the animation so a mid-flight frame cannot repaint the item you just left
     * ---------------------------------------------------------------------------------------------- */

    var holdUntil = 0;
    var SMOOTH_HOLD = 900;

    function scrollToEntry(entry) {
      var top = entry.target.getBoundingClientRect().top + window.pageYOffset - chromeExtent();
      if (top < 0) top = 0;

      var reduced = prefersReducedMotion();
      holdUntil = Date.now() + (reduced ? 80 : SMOOTH_HOLD);

      try {
        window.scrollTo({ top: top, behavior: reduced ? 'auto' : 'smooth' });
      } catch (error) {
        window.scrollTo(0, top); // Safari < 15.4 has no options object on window.scrollTo
      }
    }

    entries.forEach(function (entry) {
      entry.link.addEventListener('click', function (event) {
        // Leave modified clicks, middle clicks and anything already handled to the browser.
        if (event.defaultPrevented) return;
        if (event.button && event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        event.preventDefault();

        setActive(entry);
        scrollToEntry(entry);

        var href = entry.link.getAttribute('href');
        if (window.history && typeof window.history.pushState === 'function') {
          try {
            window.history.pushState(null, '', href);
          } catch (error) {
            /* file:// and some embedded webviews refuse pushState; the scroll already happened */
          }
        }

        // Keyboard and screen-reader users must land IN the section, not back at the top of the tab order.
        // preventScroll keeps focus() from fighting the smooth scroll we just started.
        if (!entry.target.hasAttribute('tabindex')) {
          entry.target.setAttribute('tabindex', '-1');
        }
        try {
          entry.target.focus({ preventScroll: true });
        } catch (error) {
          /* no preventScroll support: skip focusing rather than yank the page */
        }
      });
    });

    // Back/forward through the hashes we pushed: re-run the same measured scroll.
    window.addEventListener('popstate', function () {
      var hash = window.location.hash;
      if (!hash || hash.length < 2) return;
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].link.getAttribute('href') === hash) {
          setActive(entries[i]);
          scrollToEntry(entries[i]);
          return;
        }
      }
    });

    var onScroll = throttleToFrame(function () {
      // While a click-driven scroll is in flight the active item is already known; inferring it from a
      // half-finished scroll position is exactly how the wrong item used to light up.
      if (Date.now() < holdUntil) return;
      update();
    });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      chromeSeen = 0; // the geometry genuinely changed; re-measure from scratch
      holdUntil = 0;
      update();
    }, { passive: true });
    update();
  }

  /* ----------------------------------------------------------------------------------------------------------
   * (b) REMOVED — click-to-copy for the discount code.
   *
   * The USE CODE RETINOL30 chip and the copy button beside it are gone from
   * retinol-report-offer.liquid (plan §4). The chip was a link to
   * /discount/RETINOL30?redirect=/products/retinol-a-serum-2 — it bounced a buyer off this landing
   * page onto the product page at the decision moment — and the copy button optimised for "save it
   * for later", which is not the action the campaign is paying for. Every add-to-cart on the page is
   * now the permalink that applies the code, so there is nothing to copy and no [data-rr-copy] left
   * on the page. copyText(), copyWithExecCommand() and initCopy() are deleted, not left dormant.
   * -------------------------------------------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------------------------------------------
   * (d) THE PERSISTENT PRODUCT DOCK — plan §4 "The dock — new rules"
   *
   * The page's ONE persistent buy affordance.
   *
   * REVEAL    LATE, and behind TWO gates that must both pass — see the long note under (d2) below.
   *
   * REVEAL (1) when the HERO's buy block leaves the viewport: data-rr-dock-after="rr-hero-buy", the
   *           id on .rr-hero__buy, roughly 11% of the document. The old gate was #flow-sheet at
   *           14.4% of a page whose hero promised "Free. No email required." and had no buy button;
   *           the hero now carries add-to-cart #1, so the dock's job starts the instant it scrolls
   *           away. Falls back to the element id given, then to showing from the top if it is absent.
   * REVEAL (2) AND the reader is past data-rr-dock-depth of the scrollable document (38% by default,
   *           set in the theme editor on the offer section). This is the "reduce the asking" gate.
   *           The hero's add-to-cart leaves the viewport at ~11% depth; for the whole stretch after
   *           that, the hero button is still the nearest ask a reader remembers and a fixed bar
   *           repeating it is redundant noise — a second ask on a page that has asked once. Past
   *           ~38% the reader is committed to reading, the hero is long gone, and the next in-flow
   *           ask is far enough away that a persistent convenience is worth carrying.
   *
   *           Depth is measured against (scrollHeight - innerHeight), not against a fixed pixel
   *           count, so it holds when the page grows or shrinks and on any viewport. A page too
   *           short to scroll passes the gate immediately rather than never showing the dock.
   *
   * NO TUCK   The hide-on-scroll-down behaviour is DELETED. It hid the dock during the only gesture
   *           that matters — a visitor who only ever scrolls down saw it for a single frame and then
   *           never again. DOCK_TUCKED_CLASS and the direction tracking are gone, not disabled.
   * SUPPRESS  while #offer overlaps the viewport: the real add-to-cart is right there, and a
   *           floating duplicate competing with it is worse than nothing. Not a one-way latch.
   * DISMISS   kept, sessionStorage, permanent for the session. A persistent dock with no dismiss is
   *           a disruptive-destination signal (§8).
   *
   * OCCLUSION — the audit's highest-severity non-copy finding (§9 Q11). Measured on this page at
   * 390x700: the Shopify privacy dialog (#shopify-pc__banner, z-index 2,000,000) covers y 329-700 on
   * first load; a second cookie strip (z 9998) covers y 616-700 and survives the first one being
   * accepted; the Gorgias launcher sits at x 20-90 / y 586-650; the Rebuy bubble at x 175-215 /
   * y 660-700. The dock's ADD TO CART was underneath all of them and elementFromPoint at its centre
   * returned a consent button. It was a broken buy button, not a UX preference.
   *
   * The fix does not enter the z-index race and does not paint over a consent dialog — §8 is explicit
   * that the dock must stay a control, not an overlay stacked on cookie banners or chat bubbles.
   * Instead the dock hit-tests ITS OWN BUTTON and reacts to what it finds:
   *
   *   · three points across .rr-dock__atc are passed to document.elementFromPoint;
   *   · anything returned that the dock does not contain is an occluder; we walk up to its nearest
   *     fixed/sticky ancestor and read that box's top edge;
   *   · the dock lifts by (viewportHeight - occluderTop + gap), published as --rr-dock-lift, so it
   *     climbs over furniture that lives at the bottom of the screen (cookie strips, bubbles);
   *   · if the required lift exceeds DOCK_LIFT_MAX_RATIO of the viewport the occluder is not
   *     furniture, it is a dialog covering the screen — the dock withholds itself entirely
   *     (.rr-dock--blocked) and keeps re-testing until the dialog goes away.
   *
   * The probe needs the dock painted to hit-test it, so it runs one frame after reveal and again on
   * every scroll/resize frame, on any click anywhere (dismissing a banner is a click) and on a timer
   * for the first DOCK_RECHECK_FOR_MS, because these overlays mount and unmount late.
   * -------------------------------------------------------------------------------------------------------- */

  function initDock() {
    var dock = qs(DOCK);
    if (!dock) return;
    if (sessionGet(DOCK_STORAGE_KEY) === '1') return;

    var afterId = dock.getAttribute('data-rr-dock-after') || 'rr-hero-buy';
    var suppressId = dock.getAttribute('data-rr-dock-suppress') || 'offer';
    /* THE RETIRE GATE. The id of the page's LAST in-flow add-to-cart section. From the moment its
     * top edge enters the viewport the dock is done for good: the close section is the final ask,
     * and a fixed buy bar hovering over it — and then over the newsletter and the legal
     * disclaimer below it — is the page asking a fourth time after it has already asked. */
    var untilId = dock.getAttribute('data-rr-dock-until') || '';

    // The depth gate. parseFloat tolerates an empty or malformed attribute; anything outside 0-1 is
    // not a fraction of the document and falls back to the default rather than never revealing.
    var depth = parseFloat(dock.getAttribute('data-rr-dock-depth'));
    if (!(depth >= 0) || depth > 1) depth = DOCK_DEPTH_DEFAULT;
    var gate = document.getElementById(afterId);
    var suppressor = document.getElementById(suppressId);
    var retirer = untilId ? document.getElementById(untilId) : null;
    var atc = qs(DOCK_ATC, dock);

    var visible = false;
    var blocked = false;
    var lift = 0;
    var dismissed = false;

    // The hero's buy block has left the viewport: its bottom edge has crossed the top.
    function passedGate() {
      if (!gate) return true;
      return gate.getBoundingClientRect().bottom <= 0;
    }

    /* The reader is past `depth` of the scrollable document. Measured live, so it stays correct when
     * lazy images, the FAQ accordion or a third-party widget changes the page height mid-session. */
    function passedDepth() {
      var doc = document.documentElement;
      var max = (doc.scrollHeight || 0) - (window.innerHeight || 0);
      if (max <= 0) return true;   // nothing to scroll: the gate cannot be a gate
      var y = window.pageYOffset || doc.scrollTop || 0;
      return (y / max) >= depth;
    }

    // The offer section overlaps the viewport at all.
    function offerInView() {
      if (!suppressor) return false;
      var rect = suppressor.getBoundingClientRect();
      return rect.top < (window.innerHeight || 0) && rect.bottom > 0;
    }

    /* The reader has reached the final in-flow ask. Unlike offerInView this does not un-latch when
     * the section scrolls past: once the close section's top has crossed the bottom of the
     * viewport, everything below it (close, capture, disclaimer, footer) is post-ask, and the dock
     * stays gone. It is deliberately geometric rather than a sticky boolean so that scrolling back
     * UP into the body of the page brings the dock back exactly where it was earned. */
    function reachedFinalAsk() {
      if (!retirer) return false;
      return retirer.getBoundingClientRect().top < (window.innerHeight || 0);
    }

    function setVisible(next) {
      if (next === visible) return;
      visible = next;
      if (next) {
        // The `hidden` attribute comes off once and stays off, so the slide is a real transition
        // rather than a jump. The CSS keeps it visibility:hidden while .rr-dock--visible is absent.
        if (dock.hidden) {
          dock.hidden = false;
          // next frame, so the class change animates from the off-screen state
          window.requestAnimationFrame(function () {
            if (visible) dock.classList.add(DOCK_VISIBLE_CLASS);
            probeOcclusion();
          });
          return;
        }
        dock.classList.add(DOCK_VISIBLE_CLASS);
        window.requestAnimationFrame(probeOcclusion);
      } else {
        dock.classList.remove(DOCK_VISIBLE_CLASS);
      }
    }

    function setLift(next) {
      next = Math.max(0, Math.round(next));
      if (next === lift) return;
      lift = next;
      dock.style.setProperty(DOCK_LIFT_PROP, next + 'px');
    }

    function setBlocked(next) {
      if (next === blocked) return;
      blocked = next;
      dock.classList.toggle(DOCK_BLOCKED_CLASS, next);
    }

    /* The nearest ancestor (self included) that is taken out of flow — that box, not the text node
     * inside it, is what the dock actually has to clear.
     *
     * Returns null when there is no such ancestor, and that null is load-bearing: an element in
     * normal flow is ordinary page content scrolling UNDER a fixed dock, not something sitting on
     * top of it, and treating it as an occluder makes the dock climb away from its own page. */
    function pinnedBox(node) {
      var el = node;
      while (el && el !== document.body && el.nodeType === 1) {
        var position = '';
        try {
          position = window.getComputedStyle(el).position;
        } catch (error) {
          position = '';
        }
        if (position === 'fixed' || position === 'sticky') return el;
        el = el.parentElement;
      }
      return null;
    }

    /* Hit-test the dock's own button. Returns the px of lift needed to clear whatever is on top of
     * it, or -1 when the occluder is a screen-covering dialog the dock must not try to out-stack. */
    function requiredLift() {
      if (!atc) return 0;
      var rect = atc.getBoundingClientRect();
      if (!rect.width || !rect.height) return 0;

      var viewport = window.innerHeight || document.documentElement.clientHeight || 0;
      var y = Math.round(rect.top + rect.height / 2);
      var xs = [
        Math.round(rect.left + rect.width * 0.15),
        Math.round(rect.left + rect.width * 0.5),
        Math.round(rect.left + rect.width * 0.85)
      ];

      var needed = 0;

      for (var i = 0; i < xs.length; i += 1) {
        var hit = document.elementFromPoint(xs[i], y);
        if (!hit || dock.contains(hit)) continue;

        var box = pinnedBox(hit);
        if (!box) continue;
        var boxRect = box.getBoundingClientRect();
        if (!boxRect.height) continue;


        // How far up the dock has to move for its button to sit clear of this box.
        var climb = viewport - boxRect.top + DOCK_LIFT_GAP - (viewport - rect.bottom);
        if (climb > viewport * DOCK_LIFT_MAX_RATIO) return -1;
        if (climb > needed) needed = climb;
      }

      return needed;
    }

    /* Measure from the UNLIFTED position every time, so furniture that leaves lets the dock settle
     * back down instead of staying stranded halfway up the screen, then climb in passes: lifting can
     * move the button out from under a cookie strip and straight under a chat launcher.
     *
     * The whole loop is synchronous on purpose. setLift() writes an inline custom property and the
     * getBoundingClientRect()/elementFromPoint() calls inside requiredLift() force the layout that
     * property implies, so each pass reads the position the previous pass just produced. `bottom`
     * therefore must NOT be transitioned — an animated value would still be mid-flight when the next
     * pass measured it, and the dock would double-count its own climb and then oscillate. */
    function probeOcclusion() {
      if (dismissed || !visible || dock.hidden) return;

      /* Always measure from the resting position, lift zeroed, so every pass reads the same known
       * geometry. The withheld state deliberately does NOT move the dock — .rr-dock--blocked only
       * makes it invisible and un-hittable, so it stays exactly where the probe expects it and the
       * hit test looks straight through it at whatever dialog is still up. A blocked state that
       * translated the dock off-screen would hit-test clear, unblock, find the dialog again on the
       * next pass, and flicker for as long as the dialog was open. */
      setLift(0);

      var total = 0;

      for (var pass = 0; pass < 3; pass += 1) {
        var needed = requiredLift();

        if (needed < 0) {
          // Not furniture — a dialog covering the screen. Withhold the dock rather than paint over it.
          setLift(0);
          setBlocked(true);
          return;
        }

        if (needed === 0) break;

        total += needed;
        setLift(total);
      }

      // Nothing the dock cannot climb over: let it back in, at whatever height clears the furniture.
      setBlocked(false);
      setLift(total);
    }

    function update() {
      if (dismissed) return;
      setVisible(passedGate() && passedDepth() && !offerInView() && !reachedFinalAsk());
      if (visible) probeOcclusion();
    }

    var onScroll = throttleToFrame(update);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    // A consent banner is dismissed by a click, and it is removed from the DOM a beat later.
    document.addEventListener('click', function () {
      window.setTimeout(update, 60);
      window.setTimeout(update, 400);
    }, true);

    // Third-party overlays mount and unmount long after DOMContentLoaded — poll briefly rather than
    // observing the whole document.
    var recheckStarted = Date.now();
    var recheck = window.setInterval(function () {
      if (dismissed || Date.now() - recheckStarted > DOCK_RECHECK_FOR_MS) {
        window.clearInterval(recheck);
        return;
      }
      update();
    }, DOCK_RECHECK_MS);

    // Dismissal is permanent for this browsing session.
    dock.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      if (!target.closest(DOCK_DISMISS)) return;

      event.preventDefault();
      dismissed = true;
      sessionSet(DOCK_STORAGE_KEY, '1');
      setVisible(false);
      window.clearInterval(recheck);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    });

    update();
  }

  /* ----------------------------------------------------------------------------------------------------------
   * (e) SAVE THIS — window.print()
   * -------------------------------------------------------------------------------------------------------- */

  function initPrint() {
    var buttons = qsa(PRINT);
    if (!buttons.length) return;

    // Rendered hidden in Liquid so it never shows to a visitor who cannot use it.
    buttons.forEach(function (button) {
      button.hidden = false;
    });

    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var button = target.closest(PRINT);
      if (!button) return;

      event.preventDefault();
      if (typeof window.print === 'function') {
        window.print();
      }
    });
  }

  /* ----------------------------------------------------------------------------------------------------------
   * (f) SHARE / PRINT the flow card
   *
   * The card's own copy calls it "a simple flow sheet you can screenshot and use tonight", so on mobile the
   * corner tag has to actually do something. navigator.share is the native answer (it hands the page to the
   * OS sheet, where "save to Photos"/"Print" live); everything else falls back to window.print(), which is the
   * same affordance the desktop SAVE THIS control uses and which the print stylesheet already lays out.
   *
   * Both the button and the no-JS <p> ship in the markup. The button is `hidden` in Liquid and revealed here;
   * the <p> is hidden here — so a visitor without JS keeps a readable label and never sees a dead control.
   * -------------------------------------------------------------------------------------------------------- */

  function initShare() {
    var buttons = qsa(SHARE);
    if (!buttons.length) return;

    buttons.forEach(function (button) {
      button.hidden = false;

      var card = button.closest ? button.closest('.rr-protocol__card') : null;
      var scope = card || document;
      var statics = scope.querySelectorAll(SHARE_STATIC);
      for (var i = 0; i < statics.length; i++) statics[i].hidden = true;
    });

    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var button = target.closest(SHARE);
      if (!button) return;

      event.preventDefault();

      var title = button.getAttribute('data-rr-share-title') || document.title;
      var url = window.location.href;

      if (navigator && typeof navigator.share === 'function') {
        try {
          var result = navigator.share({ title: title, url: url });
          if (result && typeof result.catch === 'function') {
            // AbortError = the visitor dismissed the sheet. Never fall through to print on a dismissal.
            result.catch(function () {});
          }
          return;
        } catch (error) {
          /* fall through to print */
        }
      }

      if (typeof window.print === 'function') window.print();
    });
  }

  /* ----------------------------------------------------------------------------------------------------------
   * (g) Sticky nav offset
   *
   * The theme's header is `position: sticky; top: 0` and hides on scroll-down by translating itself
   * -100% (assets/style.css, .header-hidden / .header-visible / .header-at-top). A static `top` on our
   * rail therefore strands it: the header slides away and the rail stays parked one header-height down,
   * leaving a gap. So instead of guessing the offset from --header-height, measure where the header
   * actually IS and follow it. This also handles the announcement bar automatically — if it sticks above
   * the header, the header's own bottom edge already accounts for it.
   *
   * Writes --rr-nav-top on <html>. The section's CSS falls back to the static calc when JS is absent.
   * -------------------------------------------------------------------------------------------------------- */

  function initNavOffset() {
    var nav = document.querySelector(NAV);
    if (!nav) return;

    var header = document.querySelector('.shopify-section--header');
    if (!header) return;

    var root = document.documentElement;
    var ticking = false;
    var last = -1;

    function measure() {
      ticking = false;
      // rect.bottom is where the header visually ends right now, mid-transform included, so the rail
      // tracks the slide instead of snapping at either end. Clamp: never negative, never past a third
      // of the viewport (a misconfigured header should not push the rail into the middle of the page).
      var bottom = header.getBoundingClientRect().bottom;
      if (bottom < 0) bottom = 0;
      var cap = window.innerHeight / 3;
      if (bottom > cap) bottom = cap;
      var value = Math.round(bottom);
      if (value === last) return;
      last = value;
      root.style.setProperty('--rr-nav-top', value + 'px');
    }

    function request() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(measure);
    }

    measure();
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request, { passive: true });

    // The header's hide/reveal is a 0.3s transform transition; rect.bottom only settles when it ends.
    header.addEventListener('transitionend', function (event) {
      if (event.propertyName === 'transform') request();
    });

    // Class flips (header-hidden/header-visible) are what start those transitions.
    if (window.MutationObserver) {
      new MutationObserver(request).observe(header, {
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    }
  }

  /* ----------------------------------------------------------------------------------------------------------
   * (h) THE ARTEFACT — force every <details data-rr-expand> open before printing
   *
   * Plan §5: everything cut from the flow was appended to the print/expanded view. The report section
   * collapses that whole payload into a native <details>, which means a closed disclosure would print as
   * one summary row and the artefact would be the one thing the artefact is missing. Browsers do not open
   * a <details> for print on their own, so do it here and put it back afterwards — the visitor's own
   * open/closed state must survive their print dialog.
   *
   * The print stylesheet carries the same unhide as a fallback for engines that never fire beforeprint
   * (older WebKit), so this is belt and braces, not the only defence.
   * -------------------------------------------------------------------------------------------------------- */

  function initExpandForPrint() {
    var restore = [];

    function openAll() {
      restore = [];
      qsa(EXPAND).forEach(function (node) {
        if (!node.open) {
          restore.push(node);
          node.open = true;
        }
      });
    }

    function closeAgain() {
      restore.forEach(function (node) {
        node.open = false;
      });
      restore = [];
    }

    if (typeof window.addEventListener === 'function') {
      window.addEventListener('beforeprint', openAll);
      window.addEventListener('afterprint', closeAgain);
    }

    // Safari/iOS fire no beforeprint: matchMedia('print') is the only hook there.
    if (typeof window.matchMedia === 'function') {
      var query = window.matchMedia('print');
      var onChange = function (event) {
        if (event.matches) { openAll(); } else { closeAgain(); }
      };
      if (typeof query.addEventListener === 'function') {
        query.addEventListener('change', onChange);
      } else if (typeof query.addListener === 'function') {
        query.addListener(onChange);
      }
    }

    // The SAVE THIS / share controls call window.print() themselves; open before they do.
    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      if (target.closest(PRINT) || target.closest(SHARE)) openAll();
    }, true);
  }

  /* ----------------------------------------------------------------------------------------------------------
   * Boot
   * -------------------------------------------------------------------------------------------------------- */

  function init() {
    initScrollSpy();
    initNavOffset();
    initDock();
    initPrint();
    initShare();
    initExpandForPrint();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
